import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/hooks/use-auth';
import {
  type AccountRosterResponse,
  type InviteWorkspaceGrant,
  accountMembersService,
} from '@/services/data/AccountMembersService';

// Exported so useAccountApproval's trust/require-approval mutations
// (consumer draft-approval, 2026-07-15) can invalidate the same roster
// query after changing fields the roster now also exposes (trusted /
// requireMemberApproval).
export const ROSTER_QUERY_KEY = ['account', 'roster'] as const;

export function useAccountRoster() {
  const { user } = useAuth();
  return useQuery<AccountRosterResponse>({
    // User-scoped key: an owner→member session switch on the same client must
    // not reuse the wrong roster. Mutations still invalidate by the
    // ROSTER_QUERY_KEY prefix, which matches every user-scoped entry.
    queryKey: [...ROSTER_QUERY_KEY, user?.id],
    queryFn: accountMembersService.fetchRoster,
    enabled: Boolean(user?.id),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });
}

export function useIsAccountOwner(): boolean {
  const { data } = useAccountRoster();
  return data?.isOwner ?? true;
}

export function useAccountOwner() {
  const { data } = useAccountRoster();
  return data?.ownerProfile ?? null;
}

export function useInviteTeammate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      email,
      avatarUrl,
      grantAllWorkspaces,
      workspaceGrants,
      resend,
      password,
    }: {
      email: string;
      avatarUrl?: string;
      // Task 6's request shape (server/api/team/invite.post.ts): full-access
      // invites send grantAllWorkspaces=true and an empty grant list; scoped
      // invites send the per-workspace roles the InviteDialog collected.
      // Both optional so callers that don't care about workspace access
      // (existing tests, any future non-workspace invite path) still compile.
      grantAllWorkspaces?: boolean;
      workspaceGrants?: InviteWorkspaceGrant[];
      // Replace the pending invite for this address with a fresh link, keeping
      // its access. The server rejects a plain re-invite as a duplicate.
      resend?: boolean;
      // Create the account now with this password (no email, no link).
      password?: string;
    }) =>
      accountMembersService.sendInvite(email, avatarUrl, {
        grantAllWorkspaces,
        workspaceGrants,
        resend,
        password,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ROSTER_QUERY_KEY });
      // A preset-password invite lands as a member immediately, with workspace
      // grants already applied; the roster's Workspaces column reads this query.
      qc.invalidateQueries({ queryKey: ['workspace', 'account-access'] });
    },
  });
}

export function useRevokeInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (inviteId: string) => accountMembersService.revokeInvite(inviteId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ROSTER_QUERY_KEY });
    },
  });
}

export function useRemoveMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (memberRowId: string) => accountMembersService.removeMember(memberRowId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ROSTER_QUERY_KEY });
    },
  });
}

export function useLeaveTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => accountMembersService.leaveTeam(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ROSTER_QUERY_KEY });
      // After leaving, brand kits (owner-scoped) must refetch so the avatar
      // context label stops showing the former owner's brand.
      qc.invalidateQueries({ queryKey: ['brand-kits'] });
    },
  });
}

export function useAcceptInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: string | { token: string; accessToken?: string }) =>
      typeof input === 'string'
        ? accountMembersService.acceptInvite(input)
        : accountMembersService.acceptInvite(input.token, input.accessToken),
    onSuccess: () => {
      // Joining changes whose account (and workspaces) every query resolves to, so
      // anything cached under the person's previous, empty account is now wrong —
      // not only the roster.
      qc.invalidateQueries();
    },
  });
}
